import { computed, ref } from 'vue';
import {
  deriveInitials,
  matchesMentionQuery,
  type NxMention,
  type NxMentionCandidate,
} from './types';

export interface UseMentionComposerOptions {
  // The full candidate source; filtering happens here. Pluggable so every
  // module brings its own people list (forge accounts, wiki authors, …).
  candidates: () => NxMentionCandidate[];
  maxCandidates?: number;
}

// Headless @-mention engine for a textarea: trigger detection ("@" at line
// start or after whitespace, no whitespace up to the caret), candidate
// filtering, picker keyboard state, label insertion, and range
// reconciliation (an edit through a mention drops it; an edit before one
// shifts it). The DOM stays with the consuming component — it forwards
// input/keydown and renders the picker from this state.
export function useMentionComposer(opts: UseMentionComposerOptions) {
  const body = ref('');
  const mentions = ref<NxMention[]>([]);
  const showPicker = ref(false);
  const pickerTrigger = ref(-1); // start offset of the current "@..." token
  const pickerQuery = ref('');
  const pickerIndex = ref(0);

  const candidates = computed(() => {
    const q = pickerQuery.value.trim();
    const all = opts.candidates() || [];
    // Match full name, word prefixes, initials/Kürzel (MS → Max Schneider), shortName.
    const list = q ? all.filter((c) => matchesMentionQuery(c, q)) : all;
    return list.slice(0, opts.maxCandidates ?? 6);
  });

  function reset(initialBody = '', initialMentions: NxMention[] = []) {
    body.value = initialBody;
    mentions.value = initialMentions.map((m) => ({ ...m }));
    closePicker();
  }

  function closePicker() {
    showPicker.value = false;
    pickerTrigger.value = -1;
    pickerQuery.value = '';
    pickerIndex.value = 0;
  }

  // Call on every textarea input with the new value + caret position.
  function handleInput(after: string, caret: number) {
    const before = body.value;
    body.value = after;
    reconcileMentions(before, after);
    detectMentionTrigger(after, caret);
  }

  // On any text edit that overlaps a stored mention's range, drop the
  // mention — users expect the structured token to disappear when they edit
  // through it.
  function reconcileMentions(before: string, after: string) {
    if (before === after) return;
    // Find the common prefix / suffix length to bound the diff.
    let prefix = 0;
    const maxPrefix = Math.min(before.length, after.length);
    while (prefix < maxPrefix && before[prefix] === after[prefix]) prefix++;
    let suffix = 0;
    while (
      suffix < before.length - prefix &&
      suffix < after.length - prefix &&
      before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
    ) {
      suffix++;
    }
    const editStart = prefix;
    const editEnd = before.length - suffix; // exclusive in `before`
    const delta = after.length - before.length;

    mentions.value = mentions.value
      .map((m) => {
        // mention entirely before the edit: untouched
        if (m.end <= editStart) return m;
        // mention entirely after the edit: shift
        if (m.start >= editEnd) {
          return { ...m, start: m.start + delta, end: m.end + delta };
        }
        // overlap → drop
        return null;
      })
      .filter((m): m is NxMention => m !== null);
  }

  function detectMentionTrigger(text: string, caret: number) {
    // Find the most recent '@' before the caret on the current line, with no
    // whitespace between it and the caret.
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === '@') {
        // The '@' must be at line start or follow whitespace.
        if (i === 0 || /\s/.test(text[i - 1])) {
          pickerTrigger.value = i;
          pickerQuery.value = text.slice(i + 1, caret);
          pickerIndex.value = 0;
          showPicker.value = true;
          return;
        }
        break;
      }
      if (/\s/.test(ch)) break;
      i--;
    }
    closePicker();
  }

  // Replaces the active "@query" token with the candidate's label and
  // records the structured range. Always leaves a trailing space after the
  // name (unless one is already there) so the next keystroke doesn't glue
  // onto the token or re-open the picker mid-name. Returns the caret offset
  // (after the space) to restore in the textarea, or null when no trigger.
  function applyCandidate(candidate: NxMentionCandidate): number | null {
    const trigger = pickerTrigger.value;
    if (trigger < 0) return null;
    const name = candidate.name || '';
    const before = body.value.slice(0, trigger);
    const after = body.value.slice(trigger + 1 + pickerQuery.value.length);
    const label = `@${name}`;
    // Don't double-space if the user already typed " " after the query or
    // we're inserting before existing whitespace.
    const needsSpace = after.length === 0 || !/^\s/.test(after);
    const inserted = needsSpace ? `${label} ` : label;
    const start = trigger;
    const end = start + label.length; // range is the token only, not the space
    body.value = before + inserted + after;
    mentions.value.push({
      accountId: candidate.id,
      name,
      initials: deriveInitials(name),
      start,
      end,
      label,
    });
    closePicker();
    return start + inserted.length;
  }

  // Picker keyboard handling; returns the caret offset to restore when a
  // candidate was applied, true when the event was consumed otherwise,
  // false when the caller should handle the key itself.
  //
  // ArrowDown/Up cycle the highlight, Enter/Tab confirm, Escape closes.
  // Callers must preventDefault via the returned truthy path (we always
  // preventDefault for consumed keys so Enter never inserts a newline).
  function handleKeydown(ev: KeyboardEvent): number | boolean {
    if (!showPicker.value || candidates.value.length === 0) return false;
    // Clamp index if the filtered list shrank while the picker was open.
    if (pickerIndex.value >= candidates.value.length) {
      pickerIndex.value = Math.max(0, candidates.value.length - 1);
    }
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      ev.stopPropagation?.();
      pickerIndex.value = (pickerIndex.value + 1) % candidates.value.length;
      return true;
    }
    if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      ev.stopPropagation?.();
      pickerIndex.value =
        (pickerIndex.value - 1 + candidates.value.length) % candidates.value.length;
      return true;
    }
    if (ev.key === 'Home') {
      ev.preventDefault();
      ev.stopPropagation?.();
      pickerIndex.value = 0;
      return true;
    }
    if (ev.key === 'End') {
      ev.preventDefault();
      ev.stopPropagation?.();
      pickerIndex.value = candidates.value.length - 1;
      return true;
    }
    if (ev.key === 'Enter' || ev.key === 'Tab') {
      ev.preventDefault();
      ev.stopPropagation?.();
      const c = candidates.value[pickerIndex.value] ?? candidates.value[0];
      if (c) {
        const caret = applyCandidate(c);
        return caret === null ? true : caret;
      }
      return true;
    }
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation?.();
      closePicker();
      return true;
    }
    return false;
  }

  return {
    body,
    mentions,
    showPicker,
    pickerIndex,
    candidates,
    reset,
    closePicker,
    handleInput,
    handleKeydown,
    applyCandidate,
  };
}
